import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { TimeEntryStatus } from '@/modules/time-tracking/types/time-tracking-approvals';

/**
 * Time entry status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class TimeEntryStatusApproved implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-emerald-500 text-emerald-500',
    };
  }
}

export class TimeEntryStatusSubmitted implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-slate-500 text-slate-500',
    };
  }
}

export class TimeEntryStatusRejected implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-red-500 text-red-500',
    };
  }
}

export class TimeEntryStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createTimeEntryStatus = (status: string): BaseStatus => {
  switch (status) {
    case TimeEntryStatus.APPROVED:
      return new TimeEntryStatusApproved();
    case TimeEntryStatus.SUBMITTED:
      return new TimeEntryStatusSubmitted();
    case TimeEntryStatus.REJECTED:
      return new TimeEntryStatusRejected();
    default:
      return new TimeEntryStatusUnknown();
  }
};

/**
 * Get localized status text for time entry status.
 */
export const getTimeEntryStatusText = (status: TimeEntryStatus): string => {
  switch (status) {
    case TimeEntryStatus.APPROVED:
      return i18n.t('authenticated.timeTracking:approvals.status.approved');
    case TimeEntryStatus.SUBMITTED:
      return i18n.t('authenticated.timeTracking:approvals.status.submitted');
    case TimeEntryStatus.REJECTED:
      return i18n.t('authenticated.timeTracking:approvals.status.rejected');
    default:
      return i18n.t('common:unknown');
  }
};

export const getTimeEntryStatusColor = (status: TimeEntryStatus): string => {
  switch (status) {
    case TimeEntryStatus.APPROVED:
      return 'emerald-500';
    case TimeEntryStatus.SUBMITTED:
      return 'slate-500';
    case TimeEntryStatus.REJECTED:
      return 'red-500';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for time entry status options.
 * Maps an array of TimeEntryStatus values to option objects with label, value, and color.
 */
export const getTimeEntryStatusOptions = createStatusOptionsMapper<TimeEntryStatus>({
  getStatusColor: getTimeEntryStatusColor,
  getStatusText: getTimeEntryStatusText,
});
